import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
  HubConnectionState,
} from "@microsoft/signalr";
import { environment } from "src/environments/environment";
import { Injectable } from "@angular/core";
import {
  LoadingController,
  Platform,
} from "@ionic/angular";
import {
  EnterInLineViewModel,
  AcceptRaceViewModel,
  RaceRequestViewModel,
  UserType,
} from "../../models/user.model";
import { Network, ConnectionStatus } from "@capacitor/network";
import {
  disconnectedLoadingProps,
} from "src/utils/util.functions";
import { UtilService } from "./util.service";
import { AuthService } from "./auth.service";
import { Events } from "./events-service.service";
import { BehaviorSubject, Observable, Observer, Subject } from "rxjs";
import { filter, share } from "rxjs/operators";
import EnterQueueRuleResult from "src/models/enterQueueRuleResult.model";
import { HubEvent, HUB_EVENTS, HUB_METHODS } from "src/models/signalr-events.model";

@Injectable({
  providedIn: "root",
})
export class SignalRService {
  public hubConnection: HubConnection;
  private enterInLineViewModel = {} as EnterInLineViewModel;
  driverIsInLine: boolean;
  private upcomingRaceNotificationActive = false;
  private attemptToReconnect = false;
  interval: NodeJS.Timeout;
  waitingUpcomingRace: boolean;
  connectionState: BehaviorSubject<HubConnectionState>;

  private _hubEvents$ = new Subject<HubEvent>();
  /** Observable stream of all hub events. Subscribe with filter(e => e.type === '...') */
  public readonly events$ = this._hubEvents$.asObservable().pipe(share());
  private _listenersRegistered = false;

  constructor(
    private _loadingController: LoadingController,
    private _authService: AuthService,
    private _utilService: UtilService,
    private platform: Platform,
    public events: Events
  ) {
    this.connectionState = new BehaviorSubject<HubConnectionState>(HubConnectionState.Disconnected);
  }

  public async setUpConnection2() {
    if (this.hubConnection.state !== HubConnectionState.Connected) {
      this.connectionState.next(this.hubConnection.state);
      return await this.hubConnection.start();
    }
  }

  public async setUpConnection(enterInLineViewModel: EnterInLineViewModel) {
    this.enterInLineViewModel = enterInLineViewModel;
    if (!this.hubConnection) {
      this.hubConnection = new HubConnectionBuilder()
        .withUrl(`${environment.API_BASE}${environment.useHubV2 ? 'gospreadhub/v2' : 'gospreadhub'}`, { accessTokenFactory: () => this._authService.getToken() })
        .configureLogging(LogLevel.Critical)
        .withKeepAliveInterval(2000)
        .withServerTimeout(18000)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: () => {
            if (this.attemptToReconnect) {
              return 3500;
            }
            return null;
          },
        })
        .build();
      this.hubConnection.onclose(async () => {
        if (this.attemptToReconnect) {
          this.connectionState.next(this.hubConnection.state);
          const noNetworkTimeout = setInterval(async () => {
            if (this.hubConnection.state !== HubConnectionState.Connected) {
              await this.connect();
            } else if (
              this.hubConnection.state === HubConnectionState.Connected
            ) {
              clearInterval(noNetworkTimeout);
            }
          }, 3500);
        }
      });
      this.hubConnection.onreconnecting(async () => {
        this.connectionState.next(this.hubConnection.state);
        if (this.attemptToReconnect) {
          // reconnecting state handling
        }
      });
      Network.addListener("networkStatusChange", async (status: ConnectionStatus) => {
        if (!status.connected) {
          this.connectionState.next(this.hubConnection.state);
        } else {
          setTimeout(async () => {
            const noNetworkTimeout = setInterval(async () => {
              this.connectionState.next(this.hubConnection.state);
              if (this.hubConnection.state !== HubConnectionState.Connected) {
                await this.hubConnection.start();
                await this.reestablishConnection();
              } else if (
                this.hubConnection.state === HubConnectionState.Connected
              ) {
                clearInterval(noNetworkTimeout);
              }
            }, 5500);
          }, 8000);
        }
      });
      this.hubConnection.onreconnected(async () => {
        this.connectionState.next(this.hubConnection.state);
        if (this.attemptToReconnect) {
          await this.reestablishConnection();
        }
      });
      this._registerListeners();
      return await this.start();
    }

    // The connection object already exists (in-app navigation, no page refresh).
    // A prior disconnect() stops it AND sets attemptToReconnect=false, but leaves
    // hubConnection non-null — so without this the socket stays Disconnected, the
    // enter-queue invoke that follows never reaches the backend, and the driver
    // silently stays out of the line (the seller then sees "no drivers"). A full
    // page refresh masks this by rebuilding from scratch. Re-start a dropped
    // connection so navigation behaves the same as a refresh.
    if (this.hubConnection.state === HubConnectionState.Disconnected) {
      return await this.start();
    }
  }

  public async connect() {
    if (this.hubConnection.state === HubConnectionState.Connected)
      return;

    await this.hubConnection.start();
    await this.reestablishConnection();
  }

  public async connect2() {
    this.attemptToReconnect = false;
    await this.hubConnection.stop();
    await this.hubConnection.start();
    this.connectionState.next(this.hubConnection.state);
    await this.reestablishConnection();
    this.attemptToReconnect = true;
  }

  public async reestablishConnection() {
    if ((await this._authService.getClaim()) === UserType.Driver) {
      // Refresh server-side SignalRConnectionId with the new connection id
      // before announcing line status, so race dispatch always targets the
      // current socket. The ReconnectDriver hub method calls SetUpConnection
      // server-side as part of its handler.
      try {
        await this.setReconnectDriver(this.enterInLineViewModel);
      } catch (_) {
        // ignore — fall through to existing flow
      }
      if (this.driverIsInLine) {
        this.enterInLineViewModel.acceptedRace = true;
        this.setDriverEnterInLine(this.enterInLineViewModel);
      } else {
        this.setDriverConnectOnly(this.enterInLineViewModel);
      }
    } else {
      this.setConnectSeller(this.enterInLineViewModel);
    }
  }

  private async start() {
    this.attemptToReconnect = true;
    this.connectionState.next(this.hubConnection.state);
    return await this.hubConnection.start();
  }

  private async closeLoadings() {
    const gambys = setInterval(async () => {
      if (!(await this._loadingController.getTop())) {
        clearInterval(gambys);
      }
      await this._loadingController.dismiss(
        await this._loadingController.getTop()
      );
    }, 1000);
  }

  /**
   * Registers all hub event listeners ONCE, routing them into the events$ stream.
   * Subsequent calls are no-ops (guarded by _listenersRegistered).
   */
  private _registerListeners(): void {
    if (this._listenersRegistered) return;
    this._listenersRegistered = true;

    const register = (name: string) =>
      this.hubConnection.on(name, (payload: any) =>
        this._hubEvents$.next({ type: name as any, payload })
      );

    Object.values(HUB_EVENTS).forEach(register);
  }

  //#region Métodos dos motoristas

  public async setDriverEnterInLine(
    enterInLineViewModel: EnterInLineViewModel
  ) {
    this.driverIsInLine = true;
    return await this.hubConnection.invoke(
      HUB_METHODS.CONNECT_DRIVER_ENTER_LINE,
      enterInLineViewModel
    );
  }

  public async setDriverConnectOnly(
    enterInLineViewModel: EnterInLineViewModel
  ) {
    return await this.hubConnection.invoke(
      HUB_METHODS.CONNECT_DRIVER_ONLY,
      enterInLineViewModel
    );
  }

  public async setReconnectDriver(enterInLineViewModel: EnterInLineViewModel) {
    return await this.hubConnection.invoke(
      HUB_METHODS.RECONNECT_DRIVER,
      enterInLineViewModel
    );
  }

  public async setRefuseRace(enterInLineViewModel: EnterInLineViewModel) {
    return await this.hubConnection.invoke(HUB_METHODS.REFUSE_RACE, enterInLineViewModel);
  }

  public async setAcceptRace(acceptRaceViewModel: AcceptRaceViewModel) {
    return await this.hubConnection.invoke<boolean>(HUB_METHODS.ACCEPT_RACE, acceptRaceViewModel);
  }

  public async setGetCountdown(enterInLineViewModel: EnterInLineViewModel) {
    return await this.hubConnection.invoke(
      HUB_METHODS.COUNTDOWN_TO_ACCEPT,
      enterInLineViewModel
    );
  }

  public async setAcceptTour(acceptRaceViewModel: AcceptRaceViewModel) {
    return await this.hubConnection.invoke<boolean>(HUB_METHODS.ACCEPT_TOUR, acceptRaceViewModel);
  }

  public async setCancelRaceRequest(cancelRaceRequest: string) {
    return await this.hubConnection.invoke(
      HUB_METHODS.CLEAR_RACE_REQUEST,
      cancelRaceRequest
    );
  }

  public async setLeaveLine(enterInLineViewModel: EnterInLineViewModel) {
    this.driverIsInLine = false;
    this.events.publish("enterLine", false);
    if (this.hubConnection) {
      return await this.hubConnection.invoke(
        HUB_METHODS.LEAVE_LINE,
        enterInLineViewModel
      );
    }
  }

  public async setLeaveLineAcceptedRace() {
    this.driverIsInLine = false;
    let line = this.enterInLineViewModel;
    line.acceptedRace = true;
    return await this.hubConnection.invoke(HUB_METHODS.LEAVE_LINE, line);
  }

  public getDriverReceiveRaceRequest(
    RaceRequest: (raceRequest: any) => void
  ): void {
    this.events$.pipe(
      filter(e => e.type === HUB_EVENTS.RACE_REQUEST)
    ).subscribe(e => RaceRequest(e.payload));
  }

  public getUpcomingRaceNotification(
    UpcomingRaceNotification: (upcomingRaceNotification: any) => void
  ): void {
    if (!this.upcomingRaceNotificationActive) {
      this.upcomingRaceNotificationActive = true;
      this.events$.pipe(
        filter(e => e.type === HUB_EVENTS.UPCOMING_RACE)
      ).subscribe(e => UpcomingRaceNotification(e.payload));
    }
  }

  public getDriverPosition(
    DriverPosition: (driverPosition: any) => void
  ): void {
    this.events$.pipe(
      filter(e => e.type === HUB_EVENTS.DRIVER_POSITION)
    ).subscribe(e => DriverPosition(e.payload));
  }

  public getCancelRaceRequest(
    CancelRaceRequest: (cancelRaceRequest: any) => void
  ): void {
    this.events$.pipe(
      filter(e => e.type === HUB_EVENTS.CANCEL_RACE_REQUEST)
    ).subscribe(e => CancelRaceRequest(e.payload));
  }

  public getDriverAcceptRace(OnAccept: (onAccept: any) => void): void {
    this.events$.pipe(
      filter(e => e.type === HUB_EVENTS.ON_ACCEPT)
    ).subscribe(e => OnAccept(e.payload));
  }

  public getDriverAcceptRaceOwnRace(
    OnAcceptOwnRace: (onAcceptOwnRace: any) => void
  ): void {
    this.events$.pipe(
      filter(e => e.type === HUB_EVENTS.ON_ACCEPT_OWN_RACE)
    ).subscribe(e => OnAcceptOwnRace(e.payload));
  }

  public getDriverAcceptRaceOwnTour(
    OnAcceptOwnTour: (onAcceptOwnTour: any) => void
  ): void {
    this.events$.pipe(
      filter(e => e.type === HUB_EVENTS.ON_ACCEPT_OWN_TOUR)
    ).subscribe(e => OnAcceptOwnTour(e.payload));
  }

  public getDriverKicked(DriverKicked: (driverKicked: any) => void): void {
    this.events$.pipe(
      filter(e => e.type === HUB_EVENTS.DRIVER_KICKED)
    ).subscribe(e => DriverKicked(e.payload));
  }

  public async setKickDriver(sellerId) {
    return await this.hubConnection.invoke(HUB_METHODS.KICK_DRIVER, sellerId);
  }

  public getVerifyConnectionStatus(
    VerifyConnectionStatus: (verifyConnectionStatus: any) => void
  ): void {
    this.events$.pipe(
      filter(e => e.type === HUB_EVENTS.VERIFY_CONNECTION)
    ).subscribe(e => VerifyConnectionStatus(e.payload));
  }

  public async setDriverTripsInDispute(driverId) {
    return await this.hubConnection.invoke(HUB_METHODS.TRIPS_IN_DISPUTE_DRIVER, driverId);
  }

  public getDriverTripsInDispute(
    DriverTripsInDispute: (driverTripsInDispute: any[]) => void
  ): void {
    this.events$.pipe(
      filter(e => e.type === HUB_EVENTS.DRIVER_TRIPS_DISPUTE)
    ).subscribe(e => DriverTripsInDispute(e.payload));
  }

  public async getEnterQueueRuleValidation(driverId: string) {
    return await this.hubConnection.invoke<EnterQueueRuleResult>(HUB_METHODS.ENTER_QUEUE_RULE_VALIDATION, driverId);
  }

  //#endregion

  //#region Métodos dos vendedores

  public getRaceRequestStatus(
    RaceRequestStatus: (raceRequestStatus: any, race: any, id: any) => void
  ): void {
    this.events$.pipe(
      filter(e => e.type === HUB_EVENTS.RACE_REQUEST_STATUS)
    ).subscribe(e => {
      const payload = e.payload as any;
      RaceRequestStatus(payload?.status, payload?.race, payload?.raceId);
    });
  }

  public async setNewRaceRequest(raceRequest: RaceRequestViewModel) {
    return await this.hubConnection.invoke(HUB_METHODS.NEW_RACE_REQUEST, raceRequest);
  }

  public async setNewRaceRequestDriver(
    raceRequestDriver: RaceRequestViewModel
  ) {
    return await this.hubConnection.invoke(
      HUB_METHODS.NEW_RACE_REQUEST_DRIVER,
      raceRequestDriver
    );
  }

  public async setNewTourRequestDriver(
    raceRequestDriver: RaceRequestViewModel
  ) {
    return await this.hubConnection.invoke(
      HUB_METHODS.NEW_TOUR_REQUEST_DRIVER,
      raceRequestDriver
    );
  }

  public async setNewTourRequest(raceRequest: RaceRequestViewModel) {
    return await this.hubConnection.invoke(HUB_METHODS.NEW_TOUR_REQUEST, raceRequest);
  }

  public async setConnectSeller(enterInLineViewModel: EnterInLineViewModel) {
    return await this.hubConnection.invoke(
      HUB_METHODS.CONNECT_SELLER,
      enterInLineViewModel
    );
  }

  //#endregion

  public getCancelledRace(CancelTrip: (cancelTrip: any) => void): void {
    this.events$.pipe(
      filter(e => e.type === HUB_EVENTS.CANCEL_TRIP)
    ).subscribe(e => CancelTrip(e.payload));
  }

  public async tempDisconnectSeller() {
    this.attemptToReconnect = false;
    return await this.hubConnection.stop();
  }

  public async disconnect(): Promise<any> {
    this.driverIsInLine = false;
    this.attemptToReconnect = false;
    this.upcomingRaceNotificationActive = false;
    if (this.hubConnection) {
      return await this.hubConnection.stop();
    }
  }

  public clearHub() {
    this._listenersRegistered = false;
    this.hubConnection = null;
  }

  public async keepAlive() {
    await this.hubConnection.send(HUB_METHODS.CHECK_CONNECTION);
  }

  public connectionStateObs() {
    return new Observable((observer: Observer<HubConnectionState>) => {
      observer.next(this.hubConnection.state);
      observer.complete();
    });
  }
}
